import { useRef, useState } from "react";

import { Alert, Button, Label, TextInput } from "@/ui";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { useAuth } from "@/contexts/auth-context";
import GoogleSignInButton from "@/features/auth/google-sign-in-button";

export default function Signup() {
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const passwordConfirmRef = useRef<HTMLInputElement>(null);
  const { signup, signInWithGoogle } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();

    if (passwordRef.current!.value !== passwordConfirmRef.current!.value) {
      return setError("Passwords do not match");
    }

    try {
      setError("");
      setLoading(true);
      await signup(emailRef.current!.value, passwordRef.current!.value);
      navigate(searchParams.get("redirect") || "/workouts");
    } catch (err) {
      setError("Failed to create an account");
    }

    setLoading(false);
  }

  async function handleContinueWithGoogleClicked(
    e: React.MouseEvent<HTMLButtonElement>
  ) {
    e.preventDefault();
    try {
      setError("");
      setLoading(true);
      await signInWithGoogle();
      navigate(searchParams.get("redirect") || "/workouts");
    } catch (err) {
      setError("Failed to create an account");
    }

    setLoading(false);
  }

  return (
    <>
      <section className="w-full min-h-full">
        <div className="flex flex-col items-center justify-center px-6 py-8 mx-auto">
          <a
            href="/"
            className="flex items-center mb-6 text-2xl text-ink-primary font-extrabold tracking-tight"
          >
            Lift Notes
          </a>
          {searchParams.get("redirect") !== null && (
            <span className="mb-3">Log in to proceed.</span>
          )}
          <div className="w-full bg-surface-card rounded-card border border-line-subtle md:mt-0 sm:max-w-md xl:p-0">
            <div className="p-6 space-y-4 md:space-y-6 sm:p-8">
              <h1 className="text-xl font-bold leading-tight tracking-tight text-ink-primary md:text-2xl">
                Create an account
              </h1>
              <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                {error && (
                  <Alert color="failure">
                    <span>{error}</span>
                  </Alert>
                )}
                <div>
                  <div className="mb-2 block">
                    <Label htmlFor="email1">Email</Label>
                  </div>
                  <TextInput
                    id="email1"
                    type="email"
                    ref={emailRef}
                    placeholder="name@swole.net"
                    required={true}
                    autoComplete="on"
                  />
                </div>
                <div>
                  <div className="mb-2 block">
                    <Label htmlFor="password1">Password</Label>
                  </div>
                  <TextInput
                    id="password1"
                    type="password"
                    ref={passwordRef}
                    required={true}
                    autoComplete="on"
                  />
                </div>
                <div>
                  <div className="mb-2 block">
                    <Label htmlFor="confirm-password">Confirm Password</Label>
                  </div>
                  <TextInput
                    id="confirm-password"
                    type="password"
                    ref={passwordConfirmRef}
                    required={true}
                    autoComplete="on"
                  />
                </div>
                <div className="pt-2.5">
                  <Button
                    variant="gradient"
                    disabled={loading}
                    type="submit" fullWidth
                  >
                    Create an account
                  </Button>
                </div>
                <div className="flex items-center my-3 before:flex-1 before:border-t before:border-line-strong before:mt-0.5 after:flex-1 after:border-t after:border-line-strong after:mt-0.5">
                  <p className="text-center font-semibold mx-4 mb-0 text-ink-secondary">
                    OR
                  </p>
                </div>
                <GoogleSignInButton
                  disabled={loading}
                  onClick={handleContinueWithGoogleClicked}
                />
                <p className="text-sm font-light text-ink-muted">
                  Already have an account?{" "}
                  <Link
                    to={`/login?${searchParams.toString()}`}
                    className="font-medium text-ink-primary hover:underline"
                  >
                    Login here
                  </Link>
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
